import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CABLE_JENIS, CABLE_JENIS_LABEL, INFRA_STATUS, STATUS_LABEL, SELECT_CLS, fmtLen, pathLength } from "@/lib/network";
import { draftPath } from "@/components/network/CableDrawBar";

const blank = { kode: "", nama: "", jenis: "DISTRIBUSI", jumlah_core: "", status: "AKTIF", catatan: "" };

export function CableFormDialog({ draft, open, onClose, onSaved, backLabel = "Kembali ke Peta" }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const ed = draft?.editing;
  useEffect(() => {
    if (open) setForm(ed ? { ...blank, ...ed, nama: ed.nama || "", jumlah_core: ed.jumlah_core ?? "", catatan: ed.catatan || "" } : blank);
  }, [open, ed]);
  if (!draft) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      kode: (form.kode || "").trim(), nama: form.nama, jenis: form.jenis, status: form.status, catatan: form.catatan,
      jumlah_core: form.jumlah_core === "" ? null : Number(form.jumlah_core),
      from_kind: draft.from.kind, from_id: draft.from.id, to_kind: draft.to.kind || "odp", to_id: draft.to.id, route: draft.points,
    };
    setSaving(true);
    try {
      const r = ed ? await api.put(`/network/cables/${ed.id}`, payload) : await api.post("/network/cables", payload);
      toast.success(`Jalur kabel ${r.data.kode} tersimpan`);
      onSaved(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cable-form-dialog">
        <DialogHeader><DialogTitle>{ed ? `Edit Jalur Kabel ${ed.kode}` : "Simpan Jalur Kabel"}</DialogTitle></DialogHeader>
        <div className="rounded bg-[#F8F9FA] p-2 text-xs" data-testid="cable-form-summary">
          <b>{draft.from.kode}</b> ({draft.from.kind.toUpperCase()}) → <b>{draft.to.kode}</b> ({(draft.to.kind || "odp").toUpperCase()}) · {draft.points.length} belokan ·
          panjang ±<b>{fmtLen(pathLength(draftPath(draft)))}</b>{(draft.from.in_server || draft.to.in_server) && <span data-testid="cable-form-len-estimate"> (perkiraan — ujung di dalam SERVER/FDT)</span>}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div><Label>Kode Kabel</Label>
              <Input value={form.kode || ""} maxLength={40} className="font-mono-tab uppercase placeholder:normal-case placeholder:font-sans" data-testid="cable-form-kode"
                     onChange={(e) => setForm((f) => ({ ...f, kode: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                     placeholder={ed ? ed.kode : "Otomatis (KBL-…)"} />
              {!ed && <p className="mt-1 text-[10px] text-[#6B7280]" data-testid="cable-form-kode-hint">Kosongkan untuk kode otomatis (KBL-xxx).</p>}</div>
            <div><Label>Nama (opsional)</Label>
              <Input value={form.nama} onChange={set("nama")} data-testid="cable-form-nama" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Jenis Kabel</Label>
              <select className={`${SELECT_CLS} w-full`} value={form.jenis} onChange={set("jenis")} data-testid="cable-form-jenis">
                {CABLE_JENIS.map((j) => <option key={j} value={j}>{CABLE_JENIS_LABEL[j]}</option>)}</select></div>
            <div><Label>Jumlah Core</Label>
              <Input type="number" min="1" max="576" value={form.jumlah_core} onChange={set("jumlah_core")} placeholder="mis. 24" data-testid="cable-form-core" /></div>
            <div><Label>Status</Label>
              <select className={`${SELECT_CLS} w-full`} value={form.status} onChange={set("status")} data-testid="cable-form-status">
                {INFRA_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select></div>
          </div>
          <div><Label>Catatan</Label>
            <Textarea rows={2} value={form.catatan} onChange={set("catatan")} data-testid="cable-form-catatan" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cable-form-back">{backLabel}</Button>
            <Button type="submit" disabled={saving} data-testid="cable-form-submit">{saving ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
